/**
  *  Read More JS
  *  Truncates text via specfied character length with more/less actions.
  *  Maintains original format of pre truncated text.
  */

class ReadMore extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelectorAll('[data-rm-text]');
    if (this.content.length === 0) return;

    this.originalContentArr = [];
    this.truncatedContentArr = [];
    this.words = this.dataset.rmWords;
    this.moreLink = this.dataset.rmMorelink;
    this.lessLink = this.dataset.rmLesslink;

    if (this.hasAttribute('data-is-mobile-only') && window.innerWidth >= 1024) return;

    this.truncateText();
  }

  /**
    * @param {string} str - Target content string.
    * @returns {number} - Number of words in the string
    */
  countWords(str) {
    return str.split(/\s+/).length;
  }

  /**
    * Truncates content to specified number of words and adds ellipsis
    * @param {string} str - Content string.
    * @param {number} wordsNum - Number of words to show before truncation.
    * @returns {string} - Truncated content with ellipsis
    */
  ellipseContent(str, wordsNum) {
    return str.split(/\s+/).slice(0, wordsNum).join(' ') + '...';
  }

  /**
    * Returns the icon for expanded state
    * @returns {string} - SVG icon markup
    */
  getIconDown() {
    return `<svg class="icon icon--xs" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <path fill="currentColor" fill-rule="evenodd" d="M5.293 8.293a1 1 0 0 1 1.414 0L12 13.586l5.293-5.293a1 1 0 1 1 1.414 1.414l-6 6a1 1 0 0 1-1.414 0l-6-6a1 1 0 0 1 0-1.414Z" clip-rule="evenodd"/>
    </svg>`;
  }

  /**
    * Returns the icon for collapsed state
    * @returns {string} - SVG icon markup
    */
  getIconUp() {
    return `<svg class="icon icon--xs" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <path fill="currentColor" fill-rule="evenodd" d="M18.707 15.707a1 1 0 0 1-1.414 0L12 10.414l-5.293 5.293a1 1 0 0 1-1.414-1.414l6-6a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1 0 1.414Z" clip-rule="evenodd"/>
    </svg>`;
  }

  /**
    * Truncate and ellipses content based on specified word count.
    * Calls createLink() and handleClick() methods.
    */
  truncateText() {
    const numberOfWords = parseInt(this.words, 10);
    this.content.forEach((contentElement, i) => {
      const originalContent = contentElement.innerHTML;
      const truncateContent = this.ellipseContent(originalContent, numberOfWords);
      const originalContentWords = this.countWords(originalContent);

      this.originalContentArr.push(originalContent);
      this.truncatedContentArr.push(truncateContent);

      if (numberOfWords < originalContentWords) {
        contentElement.innerHTML = this.truncatedContentArr[i];
        this.createLink(i);
      }
    });
    this.handleClick();
  }

  /**
    * Creates and Inserts Read More Link
    * @param {number} index - Index reference of looped item
    */
  createLink(index) {
    const uniqueId = `read-more_${Math.random().toString(36).slice(2, 11)}_${index}`;

    const linkWrap = document.createElement('span');
    linkWrap.className = 'read-more__link-wrap';
    linkWrap.innerHTML = `
      <button type="button" id="${uniqueId}" class="read-more__link button button--link" style="cursor: pointer;" data-index="${index}">
        ${this.moreLink} ${this.getIconDown()}
      </button>
    `;

    // Insert created link
    this.content[index].parentNode.insertBefore(linkWrap, this.content[index].nextSibling);
  }

  /**
    * Sets up click event handlers for read more/less buttons
    */
  handleClick() {
    const readMoreLink = this.querySelectorAll('.read-more__link');

    readMoreLink.forEach((link) => {
      link.addEventListener('click', (event) => {
        // Use currentTarget to ensure we get the button, not a child element (like SVG)
        const button = event.currentTarget || event.target.closest('.read-more__link');
        this.toggleContent(button);
      });
    });
  }

  /**
    * Toggles between expanded and collapsed states
    * @param {HTMLElement} button - The clicked button element
    */
  toggleContent(button) {
    const index = parseInt(button.dataset.index, 10);
    const contentElement = this.content[index];

    contentElement.classList.toggle('is-expanded');

    if (button.dataset.clicked !== 'true') {
      contentElement.innerHTML = this.originalContentArr[index];
      button.innerHTML = `${this.lessLink} ${this.getIconUp()}`;
      button.dataset.clicked = 'true';
    } else {
      contentElement.innerHTML = this.truncatedContentArr[index];
      button.innerHTML = `${this.moreLink} ${this.getIconDown()}`;
      button.dataset.clicked = 'false';
    }
  }

  /**
    * Method to expand all instances on the page.
    */
  openAll() {
    const instances = this.querySelectorAll('.read-more__link');
    instances.forEach((instance, i) => {
      this.content[i].innerHTML = this.truncatedContentArr[i];
      instance.innerHTML = this.moreLink;
    });
  }
}
if (!customElements.get('read-more')) {
  customElements.define('read-more', ReadMore);
}
